import { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SKILL_LEVELS, LANG_LEVELS } from './cv-data';

const Field = ({ label, children, required, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
      {label} {required && <span className="text-red-400">*</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

const RepeatingSection = ({ items, onChange, emptyItem, renderItem, addLabel, testPrefix }) => (
  <div className="space-y-4" data-testid={`step-${testPrefix}`}>
    {items.map((item, i) => (
      <div key={i} className="border border-gray-200 rounded-xl p-5 relative group bg-white">
        <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity p-1"
          data-testid={`${testPrefix}-delete-${i}`}>
          <Trash2 className="w-4 h-4" />
        </button>
        {renderItem(item, i, (key, val) => {
          const copy = [...items];
          copy[i] = { ...copy[i], [key]: val };
          onChange(copy);
        })}
      </div>
    ))}
    <Button variant="outline" onClick={() => onChange([...items, { ...emptyItem }])}
      className="w-full border-dashed border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/5"
      data-testid={`${testPrefix}-add`}>
      <Plus className="w-4 h-4 mr-2" /> {addLabel}
    </Button>
  </div>
);

export const PersonalStep = ({ data, onChange }) => {
  const u = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5" data-testid="step-personal">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Prenom" required><Input value={data.firstName} onChange={e => u('firstName', e.target.value)} placeholder="Jean" data-testid="cv-firstname" /></Field>
        <Field label="Nom" required><Input value={data.lastName} onChange={e => u('lastName', e.target.value)} placeholder="Dupont" data-testid="cv-lastname" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email" required><Input type="email" value={data.email} onChange={e => u('email', e.target.value)} placeholder="jean@email.com" data-testid="cv-email" /></Field>
        <Field label="Telephone"><Input type="tel" value={data.phone} onChange={e => u('phone', e.target.value)} placeholder="87 XX XX XX" data-testid="cv-phone" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Adresse"><Input value={data.address} onChange={e => u('address', e.target.value)} placeholder="10 Rue Example" /></Field>
        <Field label="Ville"><Input value={data.city} onChange={e => u('city', e.target.value)} placeholder="Papeete" /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Code postal"><Input value={data.zip} onChange={e => u('zip', e.target.value)} placeholder="98714" /></Field>
        <Field label="Pays"><Input value={data.country} onChange={e => u('country', e.target.value)} /></Field>
      </div>
      <Field label="Resume professionnel">
        <textarea value={data.summary} onChange={e => u('summary', e.target.value)} rows={4}
          placeholder="Présentez-vous en quelques lignes..."
          className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
          data-testid="cv-summary" />
      </Field>
    </div>
  );
};

export const ExperienceStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="exp"
    emptyItem={{ company: '', position: '', startDate: '', endDate: '', current: false, description: '' }}
    addLabel="Ajouter une expérience"
    renderItem={(item, i, u) => (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Entreprise"><Input value={item.company} onChange={e => u('company', e.target.value)} placeholder="Entreprise XYZ" /></Field>
          <Field label="Poste"><Input value={item.position} onChange={e => u('position', e.target.value)} placeholder="Responsable RH" /></Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <Field label="Date début"><Input type="month" value={item.startDate} onChange={e => u('startDate', e.target.value)} /></Field>
          <Field label="Date fin"><Input type="month" value={item.endDate} onChange={e => u('endDate', e.target.value)} disabled={item.current} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-1 cursor-pointer">
            <input type="checkbox" checked={item.current} onChange={e => u('current', e.target.checked)} className="w-4 h-4 rounded text-[#0D9488]" /> Poste actuel
          </label>
        </div>
        <Field label="Description">
          <textarea value={item.description} onChange={e => u('description', e.target.value)} rows={3}
            placeholder="Décrivez vos missions et réalisations..."
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
        </Field>
      </div>
    )}
  />
);

export const EducationStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="edu"
    emptyItem={{ school: '', degree: '', field: '', startDate: '', endDate: '', description: '' }}
    addLabel="Ajouter une formation"
    renderItem={(item, i, u) => (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Établissement"><Input value={item.school} onChange={e => u('school', e.target.value)} placeholder="Université de Polynésie" /></Field>
          <Field label="Diplome"><Input value={item.degree} onChange={e => u('degree', e.target.value)} placeholder="Licence" /></Field>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Domaine"><Input value={item.field} onChange={e => u('field', e.target.value)} placeholder="Gestion des entreprises" /></Field>
          <Field label="Debut"><Input type="month" value={item.startDate} onChange={e => u('startDate', e.target.value)} /></Field>
          <Field label="Fin"><Input type="month" value={item.endDate} onChange={e => u('endDate', e.target.value)} /></Field>
        </div>
        <Field label="Description">
          <textarea value={item.description} onChange={e => u('description', e.target.value)} rows={2}
            placeholder="Details de la formation..." className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
        </Field>
      </div>
    )}
  />
);

export const SkillsStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="skill"
    emptyItem={{ name: '', level: 'Intermédiaire' }}
    addLabel="Ajouter une compétence"
    renderItem={(item, i, u) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Competence"><Input value={item.name} onChange={e => u('name', e.target.value)} placeholder="Microsoft Excel" /></Field>
        <Field label="Niveau">
          <Select value={item.level} onValueChange={v => u('level', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{SKILL_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
    )}
  />
);

export const LanguagesStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="lang"
    emptyItem={{ name: '', level: 'Intermédiaire' }}
    addLabel="Ajouter une langue"
    renderItem={(item, i, u) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Langue"><Input value={item.name} onChange={e => u('name', e.target.value)} placeholder="Francais" /></Field>
        <Field label="Niveau">
          <Select value={item.level} onValueChange={v => u('level', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{LANG_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
    )}
  />
);

export const InterestsStep = ({ data, onChange }) => {
  const [input, setInput] = useState('');
  const add = () => { const v = input.trim(); if (v) { onChange([...data, v]); setInput(''); } };
  return (
    <div className="space-y-4" data-testid="step-interests">
      <div className="flex gap-2">
        <Input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder="Ex: Randonnee, Photographie, Surf..." className="flex-1" data-testid="interest-input" />
        <Button variant="outline" onClick={add} className="border-[#0D9488] text-[#0D9488]" data-testid="interest-add">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-[#0D9488]/10 text-[#0D9488] text-sm font-medium px-3 py-1.5 rounded-full">
            {item}
            <button onClick={() => onChange(data.filter((_, idx) => idx !== i))} className="hover:text-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const ProjectsStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="proj"
    emptyItem={{ name: '', description: '', url: '' }}
    addLabel="Ajouter un projet"
    renderItem={(item, i, u) => (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nom du projet"><Input value={item.name} onChange={e => u('name', e.target.value)} placeholder="Mon projet" /></Field>
          <Field label="URL (optionnel)"><Input value={item.url} onChange={e => u('url', e.target.value)} placeholder="https://..." /></Field>
        </div>
        <Field label="Description">
          <textarea value={item.description} onChange={e => u('description', e.target.value)} rows={2}
            placeholder="Decrivez votre projet..." className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0D9488]" />
        </Field>
      </div>
    )}
  />
);

export const CertificationsStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="cert"
    emptyItem={{ name: '', issuer: '', date: '' }}
    addLabel="Ajouter une certification"
    renderItem={(item, i, u) => (
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Certification"><Input value={item.name} onChange={e => u('name', e.target.value)} placeholder="Google Analytics" /></Field>
        <Field label="Organisme"><Input value={item.issuer} onChange={e => u('issuer', e.target.value)} placeholder="Google" /></Field>
        <Field label="Date"><Input type="month" value={item.date} onChange={e => u('date', e.target.value)} /></Field>
      </div>
    )}
  />
);

export const ReferencesStep = ({ data, onChange }) => (
  <RepeatingSection items={data} onChange={onChange} testPrefix="ref"
    emptyItem={{ name: '', position: '', company: '', email: '', phone: '' }}
    addLabel="Ajouter une référence"
    renderItem={(item, i, u) => (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Nom complet"><Input value={item.name} onChange={e => u('name', e.target.value)} placeholder="Marie Martin" /></Field>
          <Field label="Poste"><Input value={item.position} onChange={e => u('position', e.target.value)} placeholder="Directrice RH" /></Field>
          <Field label="Entreprise"><Input value={item.company} onChange={e => u('company', e.target.value)} placeholder="Entreprise ABC" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Email"><Input type="email" value={item.email} onChange={e => u('email', e.target.value)} placeholder="email@example.com" /></Field>
          <Field label="Telephone"><Input value={item.phone} onChange={e => u('phone', e.target.value)} placeholder="87 XX XX XX" /></Field>
        </div>
      </div>
    )}
  />
);
